import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Pause, RotateCcw, Zap, Clock, CheckCircle, AlertCircle, Activity } from 'lucide-react';

interface PipelineStage {
  id: string;
  name: string;
  status: 'idle' | 'processing' | 'completed' | 'error';
  processingTime: number;
  throughput: number;
  errorRate: number;
  queueSize: number;
}

interface ProcessingJob {
  id: string;
  documentId: string;
  stage: string;
  progress: number;
  startTime: Date;
  estimatedCompletion: Date;
  status: 'queued' | 'processing' | 'completed' | 'failed';
}

export const RealTimeProcessingPipeline: React.FC = () => {
  const [isRunning, setIsRunning] = useState(true);
  const [stages, setStages] = useState<PipelineStage[]>([
    {
      id: 'upload',
      name: 'Document Upload',
      status: 'processing',
      processingTime: 1.2,
      throughput: 150,
      errorRate: 2.1,
      queueSize: 5
    },
    {
      id: 'classification',
      name: 'AI Classification',
      status: 'processing',
      processingTime: 3.5,
      throughput: 120,
      errorRate: 1.8,
      queueSize: 8
    },
    {
      id: 'extraction',
      name: 'Data Extraction',
      status: 'processing',
      processingTime: 5.2,
      throughput: 95,
      errorRate: 3.2,
      queueSize: 12
    },
    {
      id: 'validation',
      name: 'Validation',
      status: 'processing',
      processingTime: 2.8,
      throughput: 110,
      errorRate: 1.5,
      queueSize: 3
    },
    {
      id: 'completion',
      name: 'Completion',
      status: 'completed',
      processingTime: 0.8,
      throughput: 140,
      errorRate: 0.5,
      queueSize: 0
    }
  ]);

  const [jobs, setJobs] = useState<ProcessingJob[]>([]);

  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      // Simulate real-time updates
      setStages(prev => prev.map(stage => ({
        ...stage,
        throughput: stage.throughput + (Math.random() - 0.5) * 10,
        queueSize: Math.max(0, stage.queueSize + Math.floor((Math.random() - 0.5) * 3)),
        errorRate: Math.max(0, stage.errorRate + (Math.random() - 0.5) * 0.5)
      })));

      // Update jobs
      setJobs(prev => prev.map(job => ({
        ...job,
        progress: Math.min(100, job.progress + Math.random() * 15)
      })).filter(job => job.progress < 100));

      // Add new jobs occasionally
      if (Math.random() < 0.3) {
        const newJob: ProcessingJob = {
          id: `job-${Date.now()}`,
          documentId: `doc-${Math.floor(Math.random() * 1000)}`,
          stage: stages[Math.floor(Math.random() * stages.length)].id,
          progress: 0,
          startTime: new Date(),
          estimatedCompletion: new Date(Date.now() + Math.random() * 300000),
          status: 'processing'
        };
        setJobs(prev => [...prev.slice(-9), newJob]);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isRunning, stages]);

  const togglePipeline = () => {
    setIsRunning(!isRunning);
  };

  const resetPipeline = () => {
    setJobs([]);
    setStages(prev => prev.map(stage => ({ ...stage, queueSize: 0, status: 'idle' })));
  };

  const getStageColor = (status: string) => {
    switch (status) {
      case 'processing': return 'default';
      case 'completed': return 'success';
      case 'error': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStageIcon = (status: string) => {
    switch (status) {
      case 'processing': return <Activity className="w-4 h-4 animate-pulse" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const totalThroughput = stages.reduce((sum, stage) => sum + stage.throughput, 0);
  const avgErrorRate = stages.reduce((sum, stage) => sum + stage.errorRate, 0) / stages.length;
  const totalQueueSize = stages.reduce((sum, stage) => sum + stage.queueSize, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Real-Time Processing Pipeline</h2>
          <p className="text-muted-foreground">Monitor document processing in real-time</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={resetPipeline} variant="outline">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={togglePipeline} className="gradient-primary">
            {isRunning ? (
              <>
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Start
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Throughput</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {Math.round(totalThroughput)} <span className="text-sm font-normal">docs/hr</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Queue Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{totalQueueSize}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Error Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {avgErrorRate.toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Active Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{jobs.length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pipeline" className="w-full">
        <TabsList>
          <TabsTrigger value="pipeline">Pipeline Stages</TabsTrigger>
          <TabsTrigger value="jobs">Active Jobs</TabsTrigger>
          <TabsTrigger value="metrics">Performance Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {stages.map((stage, index) => (
              <Card key={stage.id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{stage.name}</CardTitle>
                    {getStageIcon(stage.status)}
                  </div>
                  <Badge variant={getStageColor(stage.status) as any} className="w-fit">
                    {stage.status}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Queue:</span>
                      <span className="font-medium">{stage.queueSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Throughput:</span>
                      <span className="font-medium">{Math.round(stage.throughput)}/hr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg Time:</span>
                      <span className="font-medium">{stage.processingTime}s</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Error Rate:</span>
                      <span className="font-medium text-destructive">{stage.errorRate.toFixed(1)}%</span>
                    </div>
                  </div>
                </CardContent>
                {index < stages.length - 1 && (
                  <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 hidden md:block">
                    <Zap className="w-4 h-4 text-primary" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          {jobs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No active jobs</p>
              </CardContent>
            </Card>
          ) : (
            jobs.map((job) => (
              <Card key={job.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground">Document {job.documentId}</h4>
                      <p className="text-sm text-muted-foreground capitalize">
                        Stage: {job.stage.replace('_', ' ')}
                      </p>
                    </div>
                    <Badge variant="outline">{job.status}</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span>{Math.round(job.progress)}%</span>
                    </div>
                    <Progress value={job.progress} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Started: {job.startTime.toLocaleTimeString()}</span>
                      <span>ETA: {job.estimatedCompletion.toLocaleTimeString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Processing Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stages.map((stage) => (
                    <div key={stage.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{stage.name}</span>
                        <span>{Math.round(100 - stage.errorRate)}%</span>
                      </div>
                      <Progress value={100 - stage.errorRate} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Queue Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stages.map((stage) => (
                    <div key={stage.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{stage.name}</span>
                        <span>{stage.queueSize} items</span>
                      </div>
                      <Progress 
                        value={Math.min(100, (stage.queueSize / 20) * 100)} 
                        className="h-2" 
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};